import { PaletteMode } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { darkTheme, lightTheme } from '~theme/AppTheme';

import { ThemeModeContext } from './ThemeModeContext';
import {
  readStoredThemeMode,
  ThemeModePreference,
  writeStoredThemeMode,
} from './themeModeStorage';

const DARK_SCHEME_QUERY = '(prefers-color-scheme: dark)';

function systemPrefersDark() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_SCHEME_QUERY).matches
  );
}

interface ThemeModeProviderProps {
  children: ReactNode;
}

/**
 * Owns the theme-mode preference and renders the MUI `ThemeProvider` with the
 * resolved light/dark theme. Resolution order on boot: localStorage mirror →
 * OS `prefers-color-scheme` → 'system'. Placed above the rest of the tree in
 * `main.tsx`, replacing the previous static `<ThemeProvider theme={theme}>`.
 */
export default function ThemeModeProvider({ children }: ThemeModeProviderProps) {
  const [mode, setModeState] = useState<ThemeModePreference>(
    () => readStoredThemeMode() ?? 'system',
  );
  const [systemDark, setSystemDark] = useState(() => systemPrefersDark());

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia(DARK_SCHEME_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemDark(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const resolvedMode: PaletteMode =
    mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  // Mirror the resolved mode onto <html data-theme> so SCSS (which cannot read
  // the MUI theme) can express dark-mode overrides via :root[data-theme='dark'].
  useEffect(() => {
    document.documentElement.dataset.theme = resolvedMode;
  }, [resolvedMode]);

  const setMode = useCallback((nextMode: ThemeModePreference) => {
    setModeState(nextMode);
    writeStoredThemeMode(nextMode);
  }, []);

  // Mirror the resolved mode onto <html data-theme> so SCSS-driven surfaces
  // (dispatch, toasts) follow light/dark alongside the MUI ThemeProvider.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.setAttribute('data-theme', resolvedMode);
    document.documentElement.style.colorScheme = resolvedMode;

    // An error boundary above this provider renders a light-themed fallback.
    return () => {
      document.documentElement.style.removeProperty('color-scheme');
    };
  }, [resolvedMode]);

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode],
  );

  const theme = resolvedMode === 'dark' ? darkTheme : lightTheme;

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
